import {
  meetingLanguageSchema,
  meetingTypeSchema,
  type AttendeeInput,
  type MeetingLanguage,
  type MeetingType,
} from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { directoryApi, meetingsApi } from '../lib/api.js';

type AttendeeRow = { externalName: string; roleInMeeting: string };

export function NewMeetingPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [type, setType] = useState<MeetingType>('CLIENT');
  const [language, setLanguage] = useState<MeetingLanguage>('BOTH');
  const [departmentId, setDepartmentId] = useState('');
  const [clientRef, setClientRef] = useState('');
  const [leadRef, setLeadRef] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState<AttendeeRow[]>([
    { externalName: '', roleInMeeting: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: directoryApi.departments,
  });

  const setAttendee = (i: number, patch: Partial<AttendeeRow>): void =>
    setAttendees((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const create = async (next: 'record' | 'upload'): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const cleaned: AttendeeInput[] = attendees
        .filter((a) => a.externalName.trim())
        .map((a) => ({
          externalName: a.externalName.trim(),
          roleInMeeting: a.roleInMeeting.trim() || null,
        }));
      const { meeting } = await meetingsApi.create({
        title: title.trim(),
        type,
        language,
        source: next === 'upload' ? 'UPLOAD' : 'IN_PERSON',
        departmentId: departmentId || null,
        clientRef: clientRef.trim() || null,
        leadRef: leadRef.trim() || null,
        location: location.trim() || null,
        attendees: cleaned,
      });
      navigate(`/meetings/${meeting.id}/${next === 'upload' ? 'upload' : 'record'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    void create('record');
  };

  return (
    <>
      <h1>{t('newMeeting.title')}</h1>
      {error && <div className="banner error">{error}</div>}

      <form className="card" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="title">{t('newMeeting.meetingTitle')}</label>
          <input id="title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className="row">
          <div className="field">
            <label htmlFor="type">{t('newMeeting.type')}</label>
            <select id="type" value={type} onChange={(e) => setType(e.target.value as MeetingType)}>
              {meetingTypeSchema.options.map((o) => (
                <option key={o} value={o}>
                  {t(`types.${o}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="language">{t('newMeeting.language')}</label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as MeetingLanguage)}
            >
              {meetingLanguageSchema.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="department">{t('newMeeting.department')}</label>
            <select
              id="department"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">{t('common.none')}</option>
              {departments?.items.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="field">
            <label htmlFor="clientRef">{t('newMeeting.clientRef')}</label>
            <input id="clientRef" value={clientRef} onChange={(e) => setClientRef(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="leadRef">{t('newMeeting.leadRef')}</label>
            <input id="leadRef" value={leadRef} onChange={(e) => setLeadRef(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="location">{t('newMeeting.location')}</label>
            <input id="location" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
        </div>

        <h2 style={{ marginTop: 18 }}>{t('newMeeting.attendees')}</h2>
        {attendees.map((a, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="row" key={i}>
            <div className="field">
              <label htmlFor={`att-name-${i}`}>{t('newMeeting.attendeeName')}</label>
              <input
                id={`att-name-${i}`}
                value={a.externalName}
                onChange={(e) => setAttendee(i, { externalName: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`att-role-${i}`}>{t('newMeeting.attendeeRole')}</label>
              <input
                id={`att-role-${i}`}
                value={a.roleInMeeting}
                onChange={(e) => setAttendee(i, { roleInMeeting: e.target.value })}
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="secondary"
          onClick={() => setAttendees((r) => [...r, { externalName: '', roleInMeeting: '' }])}
        >
          + {t('newMeeting.addAttendee')}
        </button>

        <div className="row" style={{ marginTop: 20 }}>
          <button type="submit" className="gold" disabled={busy || !title.trim()}>
            {t('newMeeting.create')}
          </button>
          <button
            type="button"
            className="secondary"
            disabled={busy || !title.trim()}
            onClick={() => void create('upload')}
          >
            {t('newMeeting.createUpload')}
          </button>
        </div>
      </form>
    </>
  );
}
